import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  addContactoProveedor,
  getContactosProveedor,
} from "../../api/ProveedoresyFabricas/proveedoresApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import {
  buildRfqHtmlDraft,
  buildRfqSubject,
  createAndSendEstimateRfq,
  getUniboxAccounts,
  resolvePresupuestoTemporada,
} from "../../api/Estimates/estimateRfqApi";

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_MB = 15;

function parseEmailTokens(raw) {
  return String(raw || "")
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((e) => e && e.includes("@"));
}

function mergeEmailList(values) {
  const seen = new Set();
  const out = [];
  for (const v of values || []) {
    const raw = typeof v === "string" ? v : v?.email;
    for (const e of parseEmailTokens(raw)) {
      if (seen.has(e)) continue;
      seen.add(e);
      out.push(e);
    }
  }
  return out;
}

function contactLabelFromEmail(email) {
  const local = String(email || "").split("@")[0] || "";
  const cleaned = local.replace(/[._+-]+/g, " ").trim();
  if (!cleaned) return String(email || "").trim() || "Contacto";
  return cleaned
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

const EMPTY_DETAIL_FIELDS = {
  sizes: "",
  brandedTrims: "",
  labels: "",
  fabricsTest: "",
  garmentTest: "",
  finalInspection: "",
  auditedFactory: "",
  poDate: "",
  exFd: "",
};

const DETAIL_FIELD_META = [
  { key: "sizes", labelEs: "Tallas (Sizes)", labelEn: "Sizes" },
  {
    key: "brandedTrims",
    labelEs: "Branded trims (nominated / local)",
    labelEn: "Branded trims nominated or local",
  },
  {
    key: "labels",
    labelEs: "Labels (nominated / local)",
    labelEn: "Labels nominated or local",
  },
  { key: "fabricsTest", labelEs: "Fabrics test", labelEn: "Fabrics test" },
  { key: "garmentTest", labelEs: "Garment test", labelEn: "Garment test" },
  {
    key: "finalInspection",
    labelEs: "Inspección final (3rd party / in-house)",
    labelEn: "3rd party or in-house final inspection",
  },
  {
    key: "auditedFactory",
    labelEs: "Audited factory",
    labelEn: "Audited factory",
  },
  { key: "poDate", labelEs: "PO date", labelEn: "PO date", type: "date" },
  { key: "exFd", labelEs: "ExFD", labelEn: "ExFD", type: "date" },
];

const EstimatePedirCosteDialog = ({
  open,
  onClose,
  presupuesto,
  proveedores = [],
  lines = [],
  defaultCodProv = null,
  onSent,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [codProv, setCodProv] = useState(null);
  const [contactos, setContactos] = useState([]);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [deadline, setDeadline] = useState("");
  const [lang, setLang] = useState("en");
  const [temporada, setTemporada] = useState("");
  const [detailFields, setDetailFields] = useState(EMPTY_DETAIL_FIELDS);
  const [notas, setNotas] = useState("");
  const [subject, setSubject] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [error, setError] = useState("");

  const proveedorOptions = useMemo(
    () =>
      (Array.isArray(proveedores) ? proveedores : []).map((p) => ({
        CODPROV: Number(p.CODPROV),
        label: `${p.N_PROVEEDOR || p.CODPROV}${p.PAIS ? ` (${p.PAIS})` : ""}`,
        EMAIL: p.EMAIL || "",
        N_PROVEEDOR: p.N_PROVEEDOR || "",
      })),
    [proveedores],
  );

  const selectedProveedor = useMemo(
    () =>
      proveedorOptions.find((p) => Number(p.CODPROV) === Number(codProv)) ||
      null,
    [proveedorOptions, codProv],
  );

  const lineaIds = useMemo(() => {
    const ids = (Array.isArray(lines) ? lines : [])
      .filter((l) => {
        if (!codProv) return false;
        const lineProv = Number(
          l.PROVEEDOR_ESCANDALLO ?? l.Proveedor ?? l.proveedor,
        );
        return Number.isFinite(lineProv) && lineProv === Number(codProv);
      })
      .map((l) => Number(l.IdLineaPresupuestos))
      .filter((id) => Number.isFinite(id) && id > 0);
    return [...new Set(ids)];
  }, [lines, codProv]);

  const contactEmailOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of contactos || []) {
      const email = String(c.Email || "").trim();
      if (!email) continue;
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        email,
        label: c.Nombre || email,
        puesto: c.Puesto || "",
      });
    }
    const proveedorEmail = String(selectedProveedor?.EMAIL || "").trim();
    if (proveedorEmail) {
      const key = proveedorEmail.toLowerCase();
      if (!seen.has(key)) {
        out.push({
          email: proveedorEmail,
          label: selectedProveedor?.N_PROVEEDOR || proveedorEmail,
          puesto: "",
        });
      }
    }
    return out;
  }, [contactos, selectedProveedor?.EMAIL, selectedProveedor?.N_PROVEEDOR]);

  const toEmailOptions = useMemo(() => {
    const selected = new Set(toEmails.map((e) => e.toLowerCase()));
    return contactEmailOptions.filter(
      (o) => !selected.has(String(o.email).toLowerCase()),
    );
  }, [contactEmailOptions, toEmails]);

  const ccEmailOptions = useMemo(() => {
    const selected = new Set([
      ...toEmails.map((e) => e.toLowerCase()),
      ...ccEmails.map((e) => e.toLowerCase()),
    ]);
    return contactEmailOptions.filter(
      (o) => !selected.has(String(o.email).toLowerCase()),
    );
  }, [contactEmailOptions, toEmails, ccEmails]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setSending(false);
    setSavingContact(false);
    setCcEmails([]);
    setDeadline("");
    setNotas("");
    setFiles([]);
    setLang("en");
    setDetailFields({ ...EMPTY_DETAIL_FIELDS });

    const resolved = resolvePresupuestoTemporada(presupuesto);
    setTemporada(resolved);

    // Si Temporada es un ID, resolver el nombre de catálogo
    const rawId = presupuesto?.Temporada ?? presupuesto?.TemporadaId;
    if (!resolved && rawId != null && /^\d+$/.test(String(rawId).trim())) {
      (async () => {
        try {
          const list = await getTemporadas();
          const hit = (Array.isArray(list) ? list : []).find(
            (t) =>
              String(t.ID_TEMPORADA ?? t.IdTemporada ?? "") ===
              String(rawId).trim(),
          );
          const name = hit?.TEMPORADA || hit?.Temporada || "";
          if (name) setTemporada(String(name));
        } catch {
          /* ignore */
        }
      })();
    }

    const initialCod =
      defaultCodProv != null && defaultCodProv !== ""
        ? Number(defaultCodProv)
        : presupuesto?.IdProveedor != null
          ? Number(presupuesto.IdProveedor)
          : null;
    setCodProv(Number.isFinite(initialCod) ? initialCod : null);
    setSubject(
      buildRfqSubject(presupuesto?.NumeroPresupuesto, "en", {
        diseno: presupuesto?.DiseñoMuestra || presupuesto?.designCliente || "",
      }),
    );

    let cancelled = false;
    (async () => {
      setLoadingAccounts(true);
      try {
        const list = await getUniboxAccounts();
        if (cancelled) return;
        setAccounts(list);
        const first = list.find((a) => a?.email)?.email || "";
        setFromEmail(first);
      } catch (err) {
        if (!cancelled) {
          setAccounts([]);
          setFromEmail("");
          setError(
            err?.response?.data?.error ||
              "No se pudieron cargar las cuentas TONBOX. ¿Tienes buzón conectado?",
          );
        }
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    defaultCodProv,
    presupuesto?.IdPresupuesto,
    presupuesto?.NumeroPresupuesto,
    presupuesto?.IdProveedor,
    presupuesto?.Temporada,
    presupuesto?.TemporadaNombre,
    presupuesto?.TemporadaId,
  ]);

  useEffect(() => {
    if (!open) return;
    const styleOpts = {
      diseno: presupuesto?.DiseñoMuestra || presupuesto?.designCliente || "",
    };
    setSubject((prev) => {
      const autoEs = buildRfqSubject(
        presupuesto?.NumeroPresupuesto,
        "es",
        styleOpts,
      );
      const autoEn = buildRfqSubject(
        presupuesto?.NumeroPresupuesto,
        "en",
        styleOpts,
      );
      // También regenerar si el asunto automático anterior no tenía style
      const legacyEs = buildRfqSubject(presupuesto?.NumeroPresupuesto, "es");
      const legacyEn = buildRfqSubject(presupuesto?.NumeroPresupuesto, "en");
      if (
        !prev ||
        prev === autoEs ||
        prev === autoEn ||
        prev === legacyEs ||
        prev === legacyEn
      ) {
        return buildRfqSubject(presupuesto?.NumeroPresupuesto, lang, styleOpts);
      }
      return prev;
    });
  }, [
    lang,
    open,
    presupuesto?.NumeroPresupuesto,
    presupuesto?.DiseñoMuestra,
    presupuesto?.designCliente,
  ]);

  useEffect(() => {
    if (!open || !codProv) {
      setContactos([]);
      setToEmails([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await getContactosProveedor(codProv);
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setContactos(list);
        const firstContactEmail =
          list.find((c) => c.Activo !== false && c.Email)?.Email ||
          list.find((c) => c.Email)?.Email ||
          selectedProveedor?.EMAIL ||
          "";
        const initial = String(firstContactEmail || "")
          .trim()
          .toLowerCase();
        setToEmails(initial && initial.includes("@") ? [initial] : []);
      } catch {
        if (!cancelled) {
          setContactos([]);
          const fallback = String(selectedProveedor?.EMAIL || "")
            .trim()
            .toLowerCase();
          setToEmails(fallback && fallback.includes("@") ? [fallback] : []);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, codProv, selectedProveedor?.EMAIL]);

  const knownEmailsLower = useMemo(() => {
    const set = new Set();
    for (const c of contactos || []) {
      const e = String(c.Email || "")
        .trim()
        .toLowerCase();
      if (e) set.add(e);
    }
    const pe = String(selectedProveedor?.EMAIL || "")
      .trim()
      .toLowerCase();
    if (pe) set.add(pe);
    return set;
  }, [contactos, selectedProveedor?.EMAIL]);

  const persistNewContacts = async (emails) => {
    if (!codProv) return;
    const missing = (emails || []).filter(
      (e) => e && !knownEmailsLower.has(String(e).toLowerCase()),
    );
    if (!missing.length) return;

    setSavingContact(true);
    try {
      const created = [];
      for (const email of missing) {
        try {
          const row = await addContactoProveedor(codProv, {
            Nombre: contactLabelFromEmail(email),
            Email: email,
            Activo: true,
            Comentario: "Añadido desde Pedir coste (RFQ)",
          });
          created.push(row);
        } catch (err) {
          console.error("Error creando contacto proveedor:", err);
          setError(
            err?.response?.data?.error ||
              `No se pudo guardar el contacto ${email} en la ficha del proveedor.`,
          );
        }
      }
      if (created.length) {
        setContactos((prev) => {
          const next = [...(Array.isArray(prev) ? prev : [])];
          for (const row of created) {
            const email = String(row?.Email || "")
              .trim()
              .toLowerCase();
            if (
              email &&
              !next.some(
                (c) =>
                  String(c.Email || "")
                    .trim()
                    .toLowerCase() === email,
              )
            ) {
              next.push(row);
            }
          }
          return next;
        });
      }
    } finally {
      setSavingContact(false);
    }
  };

  const handleToEmailsChange = async (_e, value) => {
    const next = mergeEmailList(value);
    setToEmails(next);
    setCcEmails((prev) =>
      prev.filter((e) => !next.includes(String(e).toLowerCase())),
    );
    await persistNewContacts(next);
  };

  const handleCcEmailsChange = async (_e, value) => {
    const next = mergeEmailList(value).filter(
      (e) => !toEmails.includes(String(e).toLowerCase()),
    );
    setCcEmails(next);
    await persistNewContacts(next);
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError("");
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= MAX_ATTACHMENTS) {
        setError(`Máximo ${MAX_ATTACHMENTS} adjuntos.`);
        break;
      }
      if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        setError(`"${file.name}" supera ${MAX_ATTACHMENT_MB} MB.`);
        continue;
      }
      const dup = next.some(
        (f) => f.name === file.name && f.size === file.size,
      );
      if (!dup) next.push(file);
    }
    setFiles(next);
  };

  const handleSend = async () => {
    setError("");
    if (!presupuesto?.IdPresupuesto) {
      setError("Guarda el presupuesto antes de pedir coste.");
      return;
    }
    if (!fromEmail) {
      setError("Selecciona la cuenta de envío (TONBOX).");
      return;
    }
    if (!toEmails.length) {
      setError("Indica al menos un email del proveedor.");
      return;
    }

    const html = buildRfqHtmlDraft({
      lang,
      numeroPresupuesto: presupuesto.NumeroPresupuesto,
      nombrePresupuesto: presupuesto.NombrePresupuesto,
      units: presupuesto.Units,
      temporada: temporada || resolvePresupuestoTemporada(presupuesto),
      diseno: presupuesto.DiseñoMuestra || presupuesto.designCliente || "",
      proveedorNombre: selectedProveedor?.N_PROVEEDOR || "",
      deadline: deadline || "",
      detailFields,
      notas,
    });

    setSending(true);
    try {
      const result = await createAndSendEstimateRfq(
        presupuesto.IdPresupuesto,
        {
          fromEmail,
          toEmail: toEmails.join(", "),
          ccEmail: ccEmails.length ? ccEmails.join(", ") : undefined,
          subject,
          html,
          lang,
          codProv: codProv || undefined,
          proveedorNombre: selectedProveedor?.N_PROVEEDOR || undefined,
          lineaIds,
          deadline: deadline || undefined,
          notes: notas || undefined,
          send: true,
        },
        files,
      );
      onSent?.(result);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo enviar la solicitud de coste",
      );
    } finally {
      setSending(false);
    }
  };

  const isEn = lang === "en";

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Pedir coste al proveedor</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="body2" fontWeight={700}>
              Idioma del email
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={lang}
              onChange={(_, v) => {
                if (v) setLang(v);
              }}
            >
              <ToggleButton value="es" sx={{ textTransform: "none", px: 1.5 }}>
                Español
              </ToggleButton>
              <ToggleButton value="en" sx={{ textTransform: "none", px: 1.5 }}>
                English
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {loadingAccounts ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel id="rfq-from-label">Enviar desde</InputLabel>
              <Select
                labelId="rfq-from-label"
                label="Enviar desde"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
              >
                {accounts.map((a) => (
                  <MenuItem key={a.id || a.email} value={a.email}>
                    {a.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Autocomplete
            options={proveedorOptions}
            value={selectedProveedor}
            onChange={(_, v) => setCodProv(v?.CODPROV ?? null)}
            getOptionLabel={(o) => o.label || ""}
            isOptionEqualToValue={(a, b) =>
              Number(a.CODPROV) === Number(b.CODPROV)
            }
            renderInput={(params) => (
              <TextField {...params} label="Proveedor" size="small" />
            )}
          />

          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            options={toEmailOptions}
            value={toEmails}
            onChange={handleToEmailsChange}
            disabled={savingContact || sending}
            getOptionLabel={(o) => {
              if (typeof o === "string") return o;
              if (!o?.email) return "";
              const base =
                o.label && o.label !== o.email
                  ? `${o.label} · ${o.email}`
                  : o.email;
              return o.puesto ? `${base} · ${o.puesto}` : base;
            }}
            isOptionEqualToValue={(a, b) => {
              const ea =
                typeof a === "string"
                  ? a.toLowerCase()
                  : String(a?.email || "").toLowerCase();
              const eb =
                typeof b === "string"
                  ? b.toLowerCase()
                  : String(b?.email || "").toLowerCase();
              return ea === eb;
            }}
            renderTags={(value, getTagProps) =>
              value.map((email, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} size="small" label={email} {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Para (emails)"
                size="small"
                helperText={
                  savingContact
                    ? "Guardando contacto en ficha proveedor…"
                    : contactos.length
                      ? `${contactos.length} contacto(s) en ficha · elige varios o escribe y Enter para guardar`
                      : "Elige contactos o escribe un email y pulsa Enter (se guarda en la ficha)"
                }
              />
            )}
          />

          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            options={ccEmailOptions}
            value={ccEmails}
            onChange={handleCcEmailsChange}
            disabled={savingContact || sending}
            getOptionLabel={(o) => {
              if (typeof o === "string") return o;
              if (!o?.email) return "";
              return o.label && o.label !== o.email
                ? `${o.label} · ${o.email}`
                : o.email;
            }}
            isOptionEqualToValue={(a, b) => {
              const ea =
                typeof a === "string"
                  ? a.toLowerCase()
                  : String(a?.email || "").toLowerCase();
              const eb =
                typeof b === "string"
                  ? b.toLowerCase()
                  : String(b?.email || "").toLowerCase();
              return ea === eb;
            }}
            renderTags={(value, getTagProps) =>
              value.map((email, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} size="small" label={email} {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="CC (opcional)"
                size="small"
                helperText="Varios emails · Enter guarda el contacto nuevo en el proveedor"
              />
            )}
          />

          <TextField
            label={isEn ? "Subject" : "Asunto"}
            size="small"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label={isEn ? "Season" : "Temporada"}
              size="small"
              fullWidth
              value={temporada}
              onChange={(e) => setTemporada(e.target.value)}
              helperText={
                !temporada ? "No venía en el presupuesto; indícala aquí" : " "
              }
            />
            <TextField
              label={isEn ? "Reply deadline" : "Fecha límite respuesta"}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </Stack>

          <Divider />
          <Typography variant="subtitle2" fontWeight={800}>
            {isEn ? "RFQ details" : "Detalle de la petición"}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            {DETAIL_FIELD_META.map((f) => (
              <TextField
                key={f.key}
                label={isEn ? f.labelEn : f.labelEs}
                size="small"
                fullWidth
                type={f.type || "text"}
                InputLabelProps={
                  f.type === "date" ? { shrink: true } : undefined
                }
                value={detailFields[f.key] || ""}
                onChange={(e) =>
                  setDetailFields((prev) => ({
                    ...prev,
                    [f.key]: e.target.value,
                  }))
                }
              />
            ))}
          </Box>

          <TextField
            label={isEn ? "Additional notes" : "Notas adicionales"}
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
          />

          <Box>
            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              disabled={sending || files.length >= MAX_ATTACHMENTS}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {isEn ? "Attach files" : "Adjuntar archivos"}
              <input
                hidden
                type="file"
                multiple
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              {isEn
                ? `Optional · up to ${MAX_ATTACHMENTS} files · max ${MAX_ATTACHMENT_MB} MB each`
                : `Opcional · hasta ${MAX_ATTACHMENTS} archivos · máx. ${MAX_ATTACHMENT_MB} MB c/u`}
            </Typography>
            {files.length ? (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {files.map((file, idx) => (
                  <Stack
                    key={`${file.name}-${file.size}-${idx}`}
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                  >
                    <Chip
                      size="small"
                      label={`${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB)`}
                      sx={{ maxWidth: "100%" }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Quitar adjunto"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Box>

          <Typography variant="caption" color="text.secondary">
            {lineaIds.length
              ? `Referencia: ${lineaIds.length} línea(s) del escandallo con este proveedor.`
              : "El coste del escandallo se introduce a mano en las líneas del presupuesto."}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={sending}>
          {sending
            ? isEn
              ? "Sending…"
              : "Enviando…"
            : isEn
              ? "Send request"
              : "Enviar solicitud"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EstimatePedirCosteDialog;
